import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, setLogLevel, enableNetwork, disableNetwork } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
import { getAnalytics, isSupported as analyticsIsSupported } from 'firebase/analytics';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported as messagingIsSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const rtdb = getDatabase(app);
export const functions = getFunctions(app, 'us-central1'); // Specify region for functions

// Set up connection error handling
if (typeof window !== 'undefined') {
  // Listen for network status changes
  window.addEventListener('online', () => {
    console.log('🌐 Network online - reconnecting to Firestore');
    enableNetwork(db).catch(err => {
      console.warn('⚠️ Failed to reconnect to Firestore:', err);
    });
  });

  window.addEventListener('offline', () => {
    console.log('🌐 Network offline - disabling Firestore');
    disableNetwork(db).catch(err => {
      console.warn('⚠️ Failed to disable Firestore:', err);
    });
  });
}

// Enable Firebase debug logging in development
if (import.meta.env.DEV) {
  setLogLevel('debug');
  console.log('🔍 Firebase debug logging enabled');
}

// Initialize Analytics only if supported (prevents IndexedDB warnings in restricted envs)
let analytics: ReturnType<typeof getAnalytics> | null = null;
if (typeof window !== 'undefined') {
  analyticsIsSupported()
    .then((supported) => {
      if (supported && import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
        try {
          analytics = getAnalytics(app);
        } catch (e) {
          console.warn('Analytics disabled:', e);
          analytics = null;
        }
      }
    })
    .catch(() => {
      // Unsupported environment, ignore
      analytics = null;
    });
}
export { analytics };

// Initialize Firebase Cloud Messaging (FCM)
let messaging: ReturnType<typeof getMessaging> | null = null;
if (typeof window !== 'undefined') {
  messagingIsSupported()
    .then((supported) => {
      if (supported && import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID) {
        try {
          // Initialize messaging - Firebase will automatically look for firebase-messaging-sw.js
          messaging = getMessaging(app);
          console.log('✅ Firebase Cloud Messaging initialized');
        } catch (e) {
          console.warn('⚠️ FCM initialization failed:', e);
          messaging = null;
        }
      }
    })
    .catch(() => {
      messaging = null;
    });
}
export { messaging };

// Connect to emulators in development only (not in production)
const useEmu = String(import.meta.env.VITE_USE_EMULATORS).toLowerCase() === 'true';
if (import.meta.env.DEV && useEmu) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectDatabaseEmulator(rtdb, 'localhost', 9000);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('Connected to Firebase emulators');
  } catch {
    // Emulators already connected
    console.log('Firebase emulators already connected or not available');
  }
}

export default app;